import type { ReactNode } from 'react'
import { cssVar } from '@/styles/tokens'

/**
 * The wizard-shape family from process/08-COMPONENT-CATALOGUE.md, one file because none of them
 * is used alone. Every value below is transcribed from Prototype_v2.html's `.wizsplit`/`.wizcard`/
 * `.wizh`/`.wizh4`/`.wizp`/`.wizbtns`/`.pf`/`.pfx` rules and process/11-TOKEN-MAP.md's fold for
 * each.
 */

/** `div.uc` → `wizard-machine`. The container holding a step-rail and its panes. **Not an
 * addressable unit** — the pane is (`E-D01`); this element carries no `data-scr`. */
export function WizardMachine({ children }: { children: ReactNode }) {
  return <div data-cmp="wizard-machine">{children}</div>
}

/** `div.wizsplit` → `wizard-split`. `grid-template-columns` and `gap: 40px` (above the space
 * scale's top step — layout, per §33.9's own rule) are out of the token map's scope; padding
 * 20px 0 10px → --sp-9 / --sp-0 / --sp-5 (0 delta each). */
export function WizardSplit({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="wizard-split" className="grid pt-9 pb-5" style={{ gridTemplateColumns: '1fr 420px', gap: '40px' }}>
      {children}
    </div>
  )
}

/** `div.wizcard` → `wizard-card`. "The pane's working surface." border-radius 4px → --r-2 (0
 * delta); padding 16px 18px 18px → --sp-8 / --sp-9 (+2px delta twice).
 *
 * **`background` corrected to the real `--gradient-card` token (W173)** — this used to
 * approximate the gradient as `linear-gradient(135deg, var(--panel), var(--strip))` because no
 * token existed for `.wizcard`'s own `linear-gradient(135deg,#FCFDFE,#F7F9FC)` at all; now that
 * `--gradient-card` exists (minted from this exact value), the approximation is replaced with the
 * real one rather than left standing beside it. */
export function WizardCard({ children }: { children: ReactNode }) {
  return (
    <div
      data-cmp="wizard-card"
      className="border border-bd rounded-2 pt-8 px-9 pb-9"
      style={{ background: 'var(--gradient-card)' }}
    >
      {children}
    </div>
  )
}

/** `h3.wizh` → `wizard-heading`. margin 0 0 8px → --sp-4 (0 delta); font-size 14px →
 * --fs-heading (+1.0px delta); weight 600 → --fw-semibold. */
export function WizardHeading({ children }: { children: ReactNode }) {
  return <h3 data-cmp="wizard-heading" className="m-0 mb-4 text-heading font-semibold">{children}</h3>
}

/** `h4.wizh4` → `wizard-subheading`. "Not a `wizard-heading` variant." margin 20px 0 6px →
 * --sp-9 / --sp-3 (0 delta each); font-size 13px → --fs-body-lg (0 delta). */
export function WizardSubheading({ children }: { children: ReactNode }) {
  return <h4 data-cmp="wizard-subheading" className="mt-9 mb-3 text-body-lg font-semibold">{children}</h4>
}

/** `p.wizp` → `wizard-prose`. margin 0 0 10px → --sp-5 (0 delta); font-size 12.5px →
 * --fs-body-lg (+0.5px delta); line-height 1.65 → --lh-relaxed (+0.05 delta). `max-width: 62ch`
 * is out of the token map's scope. */
export function WizardProse({ children }: { children: ReactNode }) {
  return (
    <p data-cmp="wizard-prose" className="mb-5 text-body-lg leading-relaxed text-tx2" style={{ maxWidth: '62ch' }}>
      {children}
    </p>
  )
}

/** `div.wizbtns` → `wizard-buttons`. "Back / Next pair." gap 10px → --sp-5 (0 delta);
 * margin-top 18px → --sp-9 (+2px delta). */
export function WizardButtons({ children }: { children: ReactNode }) {
  return <div data-cmp="wizard-buttons" className="flex gap-5 justify-end mt-9">{children}</div>
}

/** `div.kvg` → `kv-grid` (process/08-COMPONENT-CATALOGUE.md). A dense two-column key/value
 * summary, the `plan`/`order` roles' own "Restore point"/"After the restore" side panels:
 * `grid-template-columns:190px 1fr`, gap 8px 14px → --sp-4 / --sp-7 (+2px delta first),
 * `align-items:baseline` (layout). */
export function KeyValueGrid({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="kv-grid" className="grid gap-4 text-body-lg" style={{ gridTemplateColumns: '190px 1fr', alignItems: 'baseline' }}>
      {children}
    </div>
  )
}

/** `div.kk` → `kv-grid-key` (process/08-COMPONENT-CATALOGUE.md) — "the same role as `kv-key`
 * spelled differently," the catalogue's own noted near-duplicate, kept as its own name rather than
 * silently merged with `kv-key` since the two classes share no CSS rule in the prototype. */
export function KeyValueGridKey({ children }: { children: ReactNode }) {
  return (
    <div data-cmp="kv-grid-key" className="text-tx2 font-semibold">
      {children}
    </div>
  )
}

/** `div.pf` → `procedure-step`. "One numbered step of a hand-followed procedure." gap 10px →
 * --sp-5 (0 delta); padding 9px 0 → --sp-5 (+1px delta) / --sp-0; font-size 12.5px →
 * --fs-body-lg (+0.5px delta).
 *
 * `boxed` — Prototype_v2.html gives `.pf` no horizontal padding of its own, matching the base
 * padding above; every current app usage (`ob-wz-1`'s "Three things to know", `m-cancel`/
 * `m-optgap`'s own procedure lists, `PlanPane`'s "Write order") instead sits inside a bordered,
 * zero-padding `panel` (`.panel[style="padding:0"]`, the same pattern the panel's own doc comment
 * describes) and gives EACH `.pf` its own per-instance inline override instead
 * (`style="padding:10px 12px"`/`"11px 13px"`, depending on the specific instance) — found by
 * `scripts/layout-audit.mjs`'s flush-content check, which caught the number/text touching the
 * panel's own left edge on `ob-wz-1`/`m-cancel` specifically. A prop rather than a permanent
 * change to the base class, since the bare, unboxed default is the real prototype default and a
 * future non-boxed usage should still get it. */
export function ProcedureStep({ ordinal, children, boxed = false }: { ordinal: string; children: ReactNode; boxed?: boolean }) {
  return (
    <div data-cmp="procedure-step" className={`flex gap-5 items-start py-5 border-b border-bd text-body-lg last:border-b-0 ${boxed ? 'px-7' : ''}`}>
      <ProcedureStepNumber>{ordinal}</ProcedureStepNumber>
      <div>{children}</div>
    </div>
  )
}

/** `span.mono.pfx` → `procedure-step-number`. "The step ordinal." margin-top 1px → --sp-1
 * (+1px delta); `flex: 0 0 18px` is the deliberately out-of-scope `flex` family. */
export function ProcedureStepNumber({ children }: { children: ReactNode }) {
  return (
    <span data-cmp="procedure-step-number" className="mt-1 font-mono text-slate" style={{ fontSize: cssVar('fs-micro'), flex: '0 0 18px' }}>
      {children}
    </span>
  )
}
